<template lang="html">
  <div class="enhanced-tag-master-data-container">
    <!-- Upload Mode Toggle -->
    <v-card outlined class="mb-4">
      <v-card-title class="py-3">
        <v-icon class="mr-2" color="primary">mdi-upload</v-icon>
        Excel Upload Options
      </v-card-title>
      <v-card-text>
        <v-btn-toggle
          v-model="uploadMode"
          mandatory
          color="primary"
          group
          class="mb-3">
          <v-btn value="classic" class="flex-grow-1">
            <v-icon left>mdi-file-excel</v-icon>
            Classic Master Excel
          </v-btn>
          <v-btn value="flexible" class="flex-grow-1">
            <v-icon left>mdi-table-arrow-right</v-icon>
            Flexible Excel Upload
          </v-btn>
        </v-btn-toggle>
        
        <v-alert 
          :type="uploadMode === 'flexible' ? 'info' : 'warning'"
          dense 
          outlined 
          class="mb-0">
          <div v-if="uploadMode === 'flexible'">
            <strong>Flexible Upload:</strong> Upload any Excel format and map columns to AVA fields.
          </div>
          <div v-else>
            <strong>Classic Upload:</strong> Use the standard AVA Master Excel template. 
            Download, fill in data according to AVA structure, and upload.
          </div>
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Tag Type Sections -->
    <div class="master-tags-section-container">
      <!-- Hidden file inputs for both modes -->
      <input
        type="file"
        style="display: none"
        ref="masterTagUploadInput"
        id="masterTagUploadInput"
        @change="handleClassicUpload"
        accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" />
      
      <input
        type="file"
        style="display: none"
        ref="flexibleUploadInput"
        id="flexibleUploadInput"
        @change="handleFlexibleUpload"
        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, .xlsx" />

      <div class="d-flex flex-column">
        <!-- Equipment Master -->
        <div class="d-flex py-2 align-center justify-space-between">
          <v-chip
            :class="{ primary: isEquipmentMasterAvailable }"
            class="tagMasterChip d-flex justify-center px-4 py-2 rounded mr-2"
            label>
            Equipment Master
          </v-chip>
          
          <!-- Grouping options (only show for classic mode) -->
          <v-sheet
            v-if="uploadMode === 'classic'"
            color="bg1"
            class="d-flex w-50 align-center justify-center rounded mt-2 px-2">
            Grouping By:
            <v-autocomplete
              v-model="equipmentGrouping"
              multiple
              small-chips
              @blur="updateGrouping"
              :items="fields.equipment"
              item-value="id"
              item-text="text"
              placeholder="Select fields for grouping"
              background-color="bg1"
              clearable
              hide-details
              flat
              solo></v-autocomplete>
          </v-sheet>
          
          <!-- Actions -->
          <div class="d-flex">
            <v-btn
              v-if="uploadMode === 'classic'"
              @click.stop="downloadMasterExcel('Equipment')"
              title="Download Equipment Master"
              icon
              :loading="isDownloadingEquipment">
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn
              @click.stop="showUploadDialog('Equipment')"
              :title="uploadMode === 'flexible' ? 'Upload Any Excel File' : 'Upload Equipment Master'"
              icon
              v-has.any="[permissions.TagHarvester.Create, permissions.TagHarvester.Update]"
              :loading="isUploadingEquipment">
              <v-icon>{{ uploadMode === 'flexible' ? 'mdi-table-arrow-right' : 'mdi-upload' }}</v-icon>
            </v-btn>
            <v-btn
              @click.stop="deleteUntaggedMaster('Equipment')"
              title="Delete all un-tagged Equipment Master"
              icon
              v-has="permissions.TagHarvester.Delete"
              :loading="isDeletingEquipment">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Pipeline Master -->
        <div class="d-flex py-2 align-center justify-space-between">
          <v-chip
            :class="{ primary: isPipelineMasterAvailable }"
            class="tagMasterChip d-flex justify-center px-4 py-2 rounded"
            label>
            Pipeline Master
          </v-chip>
          
          <!-- Grouping options (only show for classic mode) -->
          <v-sheet
            v-if="uploadMode === 'classic'"
            color="bg1"
            class="d-flex w-50 align-center justify-center rounded mt-2 px-2">
            Grouping By:
            <v-autocomplete
              v-model="pipelineGrouping"
              multiple
              small-chips
              @blur="updateGrouping"
              :items="fields.pipeline"
              item-value="id"
              item-text="text"
              placeholder="Select fields for grouping"
              background-color="bg1"
              clearable
              hide-details
              flat
              solo></v-autocomplete>
          </v-sheet>
          
          <!-- Actions -->
          <div class="d-flex">
            <v-btn
              v-if="uploadMode === 'classic'"
              @click.stop="downloadMasterExcel('Pipeline')"
              title="Download Pipeline Master"
              icon
              :loading="isDownloadingPipeline">
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn
              @click.stop="showUploadDialog('Pipeline')"
              :title="uploadMode === 'flexible' ? 'Upload Any Excel File' : 'Upload Pipeline Master'"
              icon
              v-has.any="[permissions.TagHarvester.Create, permissions.TagHarvester.Update]"
              :loading="isUploadingPipeline">
              <v-icon>{{ uploadMode === 'flexible' ? 'mdi-table-arrow-right' : 'mdi-upload' }}</v-icon>
            </v-btn>
            <v-btn
              @click.stop="deleteUntaggedMaster('Pipeline')"
              title="Delete all un-tagged Pipeline Master"
              icon
              v-has="permissions.TagHarvester.Delete"
              :loading="isDeletingPipeline">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Structural Master -->
        <div class="d-flex py-2 align-center justify-space-between">
          <v-chip
            :class="{ primary: isStructuralMasterAvailable }"
            class="tagMasterChip d-flex justify-center px-4 py-2 rounded"
            label>
            Structural Master
          </v-chip>
          
          <!-- Actions -->
          <div class="d-flex">
            <v-btn
              v-if="uploadMode === 'classic'"
              @click.stop="downloadMasterExcel('Structural')"
              title="Download Structural Master"
              icon
              :loading="isDownloadingStructural">
              <v-icon>mdi-download</v-icon>
            </v-btn>
            <v-btn
              @click.stop="showUploadDialog('Structural')"
              :title="uploadMode === 'flexible' ? 'Upload Any Excel File' : 'Upload Structural Master'"
              icon
              v-has.any="[permissions.TagHarvester.Create, permissions.TagHarvester.Update]"
              :loading="isUploadingStructural">
              <v-icon>{{ uploadMode === 'flexible' ? 'mdi-table-arrow-right' : 'mdi-upload' }}</v-icon>
            </v-btn>
            <v-btn
              @click.stop="deleteUntaggedMaster('Structural')"
              title="Delete all un-tagged Structural Master"
              icon
              v-has="permissions.TagHarvester.Delete"
              :loading="isDeletingStructural">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- Column Mapping Dialog -->
    <ColumnMappingDialog
      v-model="showMappingDialog"
      :upload-data="currentUploadData || {}"
      :tag-type="currentTagType || 'Equipment'"
      @mapping-complete="onMappingComplete" />

    <!-- Progress Dialog -->
    <v-dialog v-model="uploadProgress.show" persistent max-width="500">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-3" color="primary">mdi-upload</v-icon>
          Processing Excel File
        </v-card-title>
        <v-card-text class="pb-2">
          <div class="mb-3">
            <v-progress-linear
              :value="uploadProgress.progress"
              height="8"
              color="primary"
              striped
              :indeterminate="uploadProgress.progress === 0">
            </v-progress-linear>
          </div>
          <div class="d-flex justify-space-between align-center">
            <span class="text-caption font-weight-medium">
              {{ uploadProgress.stage || 'Processing' }}
            </span>
            <span class="text-caption">
              {{ uploadProgress.progress }}%
            </span>
          </div>
          <p class="text-body-2 mt-2 mb-0">
            {{ uploadProgress.message || 'Processing your Excel file...' }}
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { $handleError } from '@/utils';
import HttpService from '../../../utils/httpService';
import permissions from '@Share/config/permissions';
import flexibleExcelService from '../../../features/TagHarvester/services/flexibleExcel.service';
import ColumnMappingDialog from '../../../features/TagHarvester/components/ColumnMappingDialog.vue';
import { log, warn, error } from '@/utils/debugLogger';

export default {
  name: 'EnhancedTagMasterData',
  components: {
    ColumnMappingDialog,
  },
  data() {
    return {
      permissions,
      uploadMode: 'flexible', // 'classic' or 'flexible'
      facilityUploadMode: 'single', // 'single' or 'group'
      
      // Loading states
      isDownloadingEquipment: false,
      isUploadingEquipment: false,
      isDeletingEquipment: false,
      isDownloadingPipeline: false,
      isUploadingPipeline: false,
      isDeletingPipeline: false,
      isDownloadingStructural: false,
      isUploadingStructural: false,
      isDeletingStructural: false,
      
      // Grouping data (for classic mode)
      pipelineGrouping: [],
      equipmentGrouping: [],
      structuralGrouping: [],
      
      // Flexible upload data
      showMappingDialog: false,
      currentUploadData: null,
      currentTagType: '',
      uploadProgress: {
        show: false,
        stage: '',
        progress: 0,
        message: ''
      },
      
      // Field definitions for classic mode
      fields: {
        equipment: [
          { id: 'eqTier1', text: 'Eq - Tier 1' },
          { id: 'eqTier2', text: 'Eq - Tier 2' },
          { id: 'eqTier3', text: 'Eq - Tier 3' },
          { id: 'Area', text: 'Area' },
          { id: 'Unit', text: 'Unit' },
          { id: 'Plant', text: 'Plant' },
          { id: 'FuncLocation', text: 'Function Location' },
          { id: 'System', text: 'System' },
          { id: 'Discipline', text: 'Discipline' },
          { id: 'ModelNo', text: 'Model No.' },
        ],
        pipeline: [
          { id: 'lineTier1', text: 'Line - Tier 1' },
          { id: 'lineTier2', text: 'Line - Tier 2' },
          { id: 'lineTier3', text: 'Line - Tier 3' },
          { id: 'Area', text: 'Area' },
          { id: 'Unit', text: 'Unit' },
          { id: 'Plant', text: 'Plant' },
          { id: 'FuncLocation', text: 'Function Location' },
        ],
      },
    };
  },
  computed: {
    assetId() {
      return this.$store.state.assetId;
    },
    currentFacility() {
      return this.$store.state.selectedFacility;
    },
    isEquipmentMasterAvailable() {
      return this.$store.getters.GET_IS_EQUIPMENT_MASTER_AVAILABLE;
    },
    isPipelineMasterAvailable() {
      return this.$store.getters.GET_IS_PIPELINE_MASTER_AVAILABLE;
    },
    isStructuralMasterAvailable() {
      return this.$store.getters.GET_IS_STRUCTURAL_MASTER_AVAILABLE;
    },
    tagsGrouping() {
      return {
        equipment: this.equipmentGrouping ?? [],
        pipeline: this.pipelineGrouping ?? [],
        structural: this.structuralGrouping ?? [],
      };
    },
  },
  mounted() {
    this.$store.dispatch('GET_MASTERS_STATUS');

    // Load existing grouping settings
    let tagsGrouping = this.$store.state.forgeSettings.tagsGrouping;
    this.equipmentGrouping = tagsGrouping.equipment ?? [];
    this.pipelineGrouping = tagsGrouping.pipeline ?? [];
    this.structuralGrouping = tagsGrouping.structural ?? [];

    // Load user preference for upload mode
    const savedMode = localStorage.getItem('tagUploadMode');
    if (savedMode) {
      this.uploadMode = savedMode;
    }
  },
  watch: {
    uploadMode(newMode) {
      // Save user preference
      localStorage.setItem('tagUploadMode', newMode);
    },
  },
  methods: {
    updateGrouping() {
      let stringValue = JSON.stringify(this.tagsGrouping);
      this.$store.dispatch('UPDATE_TAGS_GROUPING', stringValue);
    },

    showUploadDialog(type) {
      if (this.isUploading(type)) return;

      this.currentTagType = type;

      if (this.uploadMode === 'classic') {
        this.showClassicUpload();
      } else {
        this.showFlexibleUpload();
      }
    },

    showClassicUpload() {
      this.$refs.masterTagUploadInput.click();
    },

    showFlexibleUpload() {
      this.$refs.flexibleUploadInput.click();
    },

    async handleClassicUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      await this.uploadMasterExcel(this.currentTagType, file);
      event.target.value = ''; // Reset input
    },

    async handleFlexibleUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      await this.startFlexibleUpload(this.currentTagType, file);
      event.target.value = ''; // Reset input
    },

    async startFlexibleUpload(tagType, file) {
      this[`isUploading${tagType}`] = true;
      this.uploadProgress.show = true;

      try {
        log(`🔺 Starting flexible upload for ${tagType}`);

        const response = await flexibleExcelService.uploadAndAnalyze(file, (progress) => {
          this.uploadProgress = {
            show: true,
            stage: progress.stage,
            progress: progress.progress,
            message: progress.message
          };
        });

        this.currentUploadData = {
          uploadId: response.data.uploadId,
          fileName: file.name,
          detectedColumns: response.data.detectedColumns || [],
          sampleData: response.data.sampleData || {},
          facilitiesDetected: response.data.facilitiesDetected || [],
          facilityColumn: response.data.facilityColumn || null,
          totalRows: response.data.totalRows || 0,
          isLargeDataset: response.data.isLargeDataset || false,
        };

        this.uploadProgress.show = false;
        this.showMappingDialog = true;
      } catch (error) {
        error('Error starting flexible upload:', error);
        this.uploadProgress.show = false;
        $handleError({
          message: `Failed to analyze ${tagType} Excel file: ${error.message}`,
        });
      } finally {
        this[`isUploading${tagType}`] = false;
      }
    },

    async onMappingComplete(result) {
      try {
        this.$store.dispatch('GET_MASTERS_STATUS');

        // Emit event for parent components that might need to refresh
        this.$emit('upload-complete', {
          type: this.currentTagType,
          mode: 'flexible',
          result,
        });
      } catch (error) {
        error('Error completing mapping:', error);
      }
    },

    // Classic upload methods (existing functionality)
    downloadMasterExcel(type) {
      if (this[`isDownloading${type}`]) return;

      let downloadCallback = (includeExisting) => {
        this[`isDownloading${type}`] = true;

        log(`🔻 Downloading ${type} Master Template`);

        const link = document.createElement('a');
        link.setAttribute('download', `${type} Master.xlsx`);
        document.body.appendChild(link);

        HttpService.get({
          url: `api/tags/${type.toLowerCase()}/exportExcel/${this.assetId}?includeExisting=${includeExisting}`,
          responseType: 'blob',
        })
          .then((response) => {
            let blob = response.data;
            const objectUrl = URL.createObjectURL(blob);
            link.setAttribute('href', objectUrl);
            link.click();
            URL.revokeObjectURL(objectUrl);
          })
          .catch(({ message }) => {
            error(message);
            $handleError({
              message: `Failed to download ${type} Master Template`,
            });
          })
          .finally(() => {
            document.body.removeChild(link);
            this[`isDownloading${type}`] = false;
          });
      };

      this.$dialog.info({
        text: `You are about to download the template.\n\nDo you want to include the existing records?`,
        title: `Download Template`,
        actions: {
          false: {
            text: 'No',
            handle: () => downloadCallback(false),
          },
          true: {
            text: 'Yes',
            color: 'primary',
            handle: () => downloadCallback(true),
          },
        },
      });
    },

    async uploadMasterExcel(tagType, file) {
      if (this[`isUploading${tagType}`]) return;

      log(`🔺 Uploading ${tagType} Master Template`);

      this[`isUploading${tagType}`] = true;

      let formData = new FormData();
      formData.set('file', file);

      try {
        await HttpService.post({
          url: `api/tags/${tagType.toLowerCase()}/importExcel/${this.assetId}`,
          data: formData,
          headers: {
            'Content-Type': false,
            processData: false,
          },
        });

        this.$store.dispatch('GET_MASTERS_STATUS');
        this.$dialog.notify.success(
          `${tagType} Excel import job has been queued. You will be notified when processing is complete.`,
          {
            position: 'top-right',
            timeout: 3000,
          }
        );

        this.$emit('upload-complete', {
          type: tagType,
          mode: 'classic',
        });
      } catch (error) {
        error(error.message);
        $handleError({
          message: `An Error occurred when updating ${tagType} tags.`,
        });
      } finally {
        this[`isUploading${tagType}`] = false;
      }
    },

    deleteUntaggedMaster(type) {
      if (this[`isDeleting${type}`]) return;

      this.$dialog.error({
        text: `Are you sure you want to delete un-tagged ${type} Master?`,
        title: `Delete ${type} Master`,
        actions: {
          false: 'Cancel',
          true: {
            text: 'Ok',
            color: 'primary',
            handle: () => {
              this[`isDeleting${type}`] = true;

              return HttpService.post({
                url: `api/tags/deleteUntaggedMaster/${type}/${this.assetId}`,
              })
                .then(({ data }) => {
                  this.$store.dispatch('GET_MASTERS_STATUS');
                  this.$dialog.notify.success(
                    `${data} un-tagged ${type} tags deleted successfully.`,
                    {
                      position: 'top-right',
                      timeout: 3000,
                    }
                  );
                })
                .catch((error) => {
                  error(error.message);
                  $handleError({
                    message: `An Error occurred when deleting un-tagged ${type} tags.`,
                  });
                })
                .finally(() => {
                  this[`isDeleting${type}`] = false;
                });
            },
          },
        },
      });
    },

    isUploading(type) {
      return this[`isUploading${type}`];
    },
  },
};
</script>

<style lang="css">
.enhanced-tag-master-data-container {
  padding: 10px 10px;
}

.master-tags-section-container {
  display: flex;
  margin-inline-start: 20px;
  column-gap: 30px;
}

.tagMasterChip {
  width: 150px;
}

.v-btn-toggle .v-btn {
  border-radius: 4px !important;
}

.v-btn-toggle .v-btn:first-child {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.v-btn-toggle .v-btn:last-child {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}
</style>
